import React, { useState, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useSelector } from "react-redux";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import InputAdornment from "@material-ui/core/InputAdornment";
import ClearIcon from "@material-ui/icons/Clear";
import Autocomplete, {
  type AutocompleteProps,
} from "src/components/Autocomplete";
import type {
  Value,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from "@material-ui/lab/useAutocomplete";
import { formatDollars } from "src/utils/formatter";
import {
  linkedItemsSelector,
  linkedItemDocumentsSelector,
} from "src/store/transaction/selector";
import { getSaleById } from "src/store/sale/selector";
import { inventoryByIdSelector } from "src/store/inventory/selector";
import type { InventoryFormProps } from "./types";

const useInventoryFormStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  container: {
    flex: 1,
    marginBottom: theme.spacing(2),
    position: "relative",
    "& > table": {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
}));

function InventoryFormRow({
  type,
  id,
  onRemove,
}: {
  type: "inventory" | "sale";
  id: string;
  onRemove: () => void;
}) {
  const item = useSelector(
    type === "inventory"
      ? (state) => inventoryByIdSelector(state, id)
      : (state) => getSaleById(state, id)
  );
  return (
    <TableRow>
      <TableCell>{item?.item_title}</TableCell>
      <TableCell align="right">
        {formatDollars(item?.purchase_price || 0)}
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={onRemove} tabIndex={-1}>
          <ClearIcon fontSize="inherit" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function ClearableAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  onChange,
  ...props
}: Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "onChange"
> & {
  onChange?: (
    event: any,
    value: Value<T, Multiple, DisableClearable, FreeSolo>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
    opts?: {
      setInputValue: Dispatch<SetStateAction<string>>;
    }
  ) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  return (
    <Autocomplete
      inputValue={inputValue}
      onInputChange={(e, value) => setInputValue(value)}
      onChange={(e, value, reason, details) =>
        onChange?.(e, value, reason, details, { setInputValue })
      }
      {...props}
    />
  );
}

export function InventoryForm({
  values,
  setValues,
  errors,
  loading,
}: InventoryFormProps) {
  const classes = useInventoryFormStyles();
  const items = useSelector(linkedItemsSelector);
  const valuesItems = values.items;
  const sortedItems = useMemo(() => {
    const sorted = valuesItems
      ? items.filter(
          (i) =>
            !valuesItems.find((it) => i.type === it.type && i.item.id === it.id)
        )
      : [...items];

    sorted.sort(
      (a, b) =>
        (b.item.purchase_date?.getTime?.() || 0) -
        (a.item.purchase_date?.getTime?.() || 0)
    );
    return sorted;
  }, [items, valuesItems]);
  const linkedItems = useSelector((state) =>
    linkedItemDocumentsSelector(state, values.items || [])
  );
  const totalPurchasePrice = linkedItems.reduce(
    (acc, entry) => acc + (entry.item.purchase_price || 0),
    0
  );

  return (
    <div className={classes.root}>
      <ClearableAutocomplete
        value={null}
        fullWidth
        options={sortedItems}
        disabled={loading}
        onChange={(e, value, reason, details, opts) => {
          e.preventDefault();
          if (value?.type) {
            setValues((values) => {
              if (
                values.items &&
                values.items.find(
                  (v) => v.type === value.type && v.id === value.item.id
                )
              )
                return values;
              const items = [
                ...(values.items || []),
                { type: value.type, id: value.item.id } as any,
              ];
              return {
                ...values,
                items,
              };
            });

            if (opts?.setInputValue) {
              opts.setInputValue("");
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={errors.account ? true : false}
            helperText={errors.account}
            margin="dense"
            required
            label="Linked Inventory Items"
            fullWidth
            variant="outlined"
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          />
        )}
        getOptionLabel={(option) => {
          const { type, item } = option;
          if (type === "inventory")
            return [`Current Inventory: ${item.item_title}`, item.sku]
              .filter(Boolean)
              .join("-");
          if (type === "sale")
            return [`Sold Inventory: ${item.item_title}`, item.sku]
              .filter(Boolean)
              .join("-");
          return "";
        }}
      />
      <TableContainer className={classes.container}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Item Title</TableCell>
              <TableCell align="right">Purchase Price</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(values.items || []).map(({ type, id }) => (
              <InventoryFormRow
                key={`${type}-${id}`}
                type={type}
                id={id}
                onRemove={() => {
                  setValues((values) =>
                    values.items
                      ? {
                          ...values,
                          items: values.items.filter(
                            (v) => v.type !== type || v.id !== id
                          ),
                        }
                      : values
                  );
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TextField
        inputProps={{ readOnly: true, tabIndex: -1 }}
        value={totalPurchasePrice.toFixed(2)}
        label="Linked Inventory Purchase Price Total"
        variant="outlined"
        fullWidth
        margin="dense"
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
      />
      <TextField
        inputProps={{ readOnly: true, tabIndex: -1 }}
        value={((values.amount || 0) - totalPurchasePrice).toFixed(2)}
        label="Difference"
        variant="outlined"
        fullWidth
        margin="dense"
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
      />
    </div>
  );
}
